import axios from 'axios'
import { LoginRequest, LoginResponse, User, Post, Category } from '../types'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://baochinhphu-backend-development.xox-labs-server.workers.dev/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },
  
  getProfile: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/profile')
    return response.data
  },
}

// Posts API
export const postsAPI = {
  getAll: async (params?: any) => {
    const response = await api.get('/posts', { params })
    return response.data
  },
  
  getById: async (id: string) => {
    const response = await api.get(`/posts/${id}`)
    return response.data
  },
  
  create: async (post: Partial<Post>) => {
    const response = await api.post('/posts', post)
    return response.data
  },
  
  update: async (id: string, post: Partial<Post>) => {
    const response = await api.put(`/posts/${id}`, post)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/posts/${id}`)
    return response.data
  },
}

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    const response = await api.get('/categories')
    return response.data
  },
  
  create: async (category: Partial<Category>) => {
    const response = await api.post('/categories', category)
    return response.data
  },
  
  update: async (id: string, category: Partial<Category>) => {
    const response = await api.put(`/categories/${id}`, category)
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/categories/${id}`)
    return response.data
  },
}

// Users API (for author selection)
export const usersAPI = {
  getAll: async () => {
    // For now, return current user only since there's no users endpoint
    const profile = await authAPI.getProfile()
    return { users: [profile.user] }
  },
}

// Media API
export const mediaAPI = {
  getAll: async (params?: any) => {
    const response = await api.get('/media', { params })
    return response.data
  },
  
  upload: async (file: File, altText?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (altText) formData.append('altText', altText)
    
    const response = await api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },
  
  delete: async (id: string) => {
    const response = await api.delete(`/media/${id}`)
    return response.data
  },
}

export default api